import React, { useState, useEffect } from 'react';
import './DashboardHome.css';
import { TrendingUp, MessageSquare } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { getCurrentTrendingVideos } from '../setupTrendingVideos';
import TargetedTrendingStyleScriptPanel from './TargetedTrendingStyleScriptPanel';
import ClientScriptPanel from './ClientScriptPanel';
import GeneralFeedbackModal from './GeneralFeedbackModal';
import toast from 'react-hot-toast';

function DashboardHome({ clientName, credits = 0, setActiveTab }) {
  const [showTrendingModal, setShowTrendingModal] = useState(false);
  const [targetedTrendingVideos, setTargetedTrendingVideos] = useState([]);
  const [selectedTargetedVideo, setSelectedTargetedVideo] = useState(null);
  const [showTargetedScriptPanel, setShowTargetedScriptPanel] = useState(false);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [hasCampaigns, setHasCampaigns] = useState(false);
  const [checkingCampaigns, setCheckingCampaigns] = useState(true);
  const [newSubmissionsCount, setNewSubmissionsCount] = useState(0);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  
  // State for opening ClientScriptPanel after script generation
  const [openScriptEditor, setOpenScriptEditor] = useState(false);
  const [generatedScriptData, setGeneratedScriptData] = useState(null);
  
  // State for video play overlays
  const [playingVideos, setPlayingVideos] = useState({});

  // Fetch targeted trending videos from Firestore
  useEffect(() => {
    const fetchTrendingVideos = async () => {
      try {
        const data = await getCurrentTrendingVideos();
        if (data && data.videos) {
          setTargetedTrendingVideos(data.videos);
        }
      } catch (error) {
        console.error('Error fetching targeted trending videos:', error);
      } finally {
        setLoadingVideos(false);
      }
    };

    fetchTrendingVideos();
  }, []);

  // Check if user has campaigns
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      setCheckingCampaigns(false);
      setHasCampaigns(false);
      return;
    }

    const campaignsRef = collection(db, 'users', user.uid, 'campaigns');
    const unsubscribe = onSnapshot(campaignsRef, (snapshot) => {
      setHasCampaigns(!snapshot.empty);
      setCheckingCampaigns(false);
    }, (error) => {
      console.error('Error checking campaigns:', error);
      setCheckingCampaigns(false);
    });

    return () => unsubscribe();
  }, []);

  // Check for new submissions in real-time
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      setNewSubmissionsCount(0);
      return;
    }

    // Listen to all campaigns to count new submissions
    const campaignsRef = collection(db, 'users', user.uid, 'campaigns');
    const unsubscribe = onSnapshot(campaignsRef, async (campaignsSnapshot) => {
      try {
        let totalNewSubmissions = 0;
        
        for (const campaignDoc of campaignsSnapshot.docs) {
          const submissionsRef = collection(db, 'users', user.uid, 'campaigns', campaignDoc.id, 'submissions');
          const submissionsSnapshot = await new Promise((resolve) => {
            const unsub = onSnapshot(submissionsRef, (snapshot) => {
              unsub();
              resolve(snapshot);
            });
          });
          
          // Count submissions that are new/unreviewed
          submissionsSnapshot.docs.forEach(doc => {
            const data = doc.data();
            if (data.status === 'pending' || data.status === 'submitted' || !data.reviewed) {
              totalNewSubmissions++;
            }
          });
        }
        
        setNewSubmissionsCount(totalNewSubmissions);
      } catch (error) {
        console.error('Error checking new submissions:', error);
        setNewSubmissionsCount(0);
      }
    });

    return () => unsubscribe();
  }, []);

  const getWelcomeMessage = () => {
    if (!clientName) return "Welcome";
    return `Welcome, ${clientName}!`;
  };

  const handleUseTargetedStyle = (video) => {
    if (!hasCampaigns) {
      alert('Please create a campaign first before using targeted trending styles.');
      return;
    }
    
    setSelectedTargetedVideo(video);
    setShowTrendingModal(false);
    setShowTargetedScriptPanel(true);
  };

  const handleScriptGenerated = (scriptData) => {
    // Script has been generated and saved
    // Open ClientScriptPanel in edit mode
    setGeneratedScriptData(scriptData);
    setOpenScriptEditor(true);
  };

  const handleVideoPlay = (videoId) => {
    setPlayingVideos(prev => ({ ...prev, [videoId]: true }));
  };

  const handleVideoPause = (videoId) => {
    setPlayingVideos(prev => ({ ...prev, [videoId]: false }));
  };

  const toggleVideoPlay = (videoId) => {
    const videoElement = document.getElementById(`trending-video-${videoId}`);
    if (videoElement) {
      if (videoElement.paused) {
        videoElement.play();
      } else {
        videoElement.pause();
      }
    }
  };

  return (
    <div className="dashboard-home-simple">
      {/* Welcome Section */}
      <div className="dashboard-home-welcome-section">
        <img src="/ugc3-logo.png" alt="UGC3" className="dashboard-home-top-logo" />
        <h1 className="dashboard-home-welcome-title">{getWelcomeMessage()}</h1>
        <p className="dashboard-home-welcome-subtitle">Manage your campaigns and track performance</p>
        <div className="dashboard-home-platform-badge">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
            <path d="M8 0L10.5 5.5L16 8L10.5 10.5L8 16L5.5 10.5L0 8L5.5 5.5L8 0Z" fill="currentColor"/>
          </svg>
          Platform
        </div>
      </div>
      
      {/* Main Cards */}
      <div className="dashboard-home-main-cards">
        <div
          className="dashboard-home-main-card"
          onClick={() => setActiveTab && setActiveTab('campaigns')}
        >
          <div className="dashboard-home-card-icon">
            <svg className="dashboard-home-lucide-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
            </svg>
          </div>
          <div className="dashboard-home-card-content">
            <h3>Start Project</h3>
            <p>Create a new campaign and brief from start to finish</p>
          </div>
        </div>

        <div 
          className="dashboard-home-main-card"
          onClick={() => {
            // Performance dashboard not available on release - show coming soon message
            toast('Tracking & Performance is coming soon! This feature will allow you to monitor your campaign performance and analytics.', {
              icon: '🚀',
              duration: 5000,
            });
          }}
        >
          <div className="dashboard-home-card-icon">
            <svg className="dashboard-home-lucide-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3v18h18"/>
              <path d="m9 9 3 3 3-3"/>
              <path d="m9 15 3-3 3 3"/>
            </svg>
          </div>
          <div className="dashboard-home-card-content">
            <h3>Tracking & Performance</h3>
            <p>Monitor your campaign performance and analytics</p>
            <div className="dashboard-home-coming-soon-banner">
              <span className="dashboard-home-coming-soon-icon">★</span>
              Coming Soon
              <span className="dashboard-home-coming-soon-icon">★</span>
            </div>
          </div>
        </div>

        {/*
          Submissions card: we treat "has new" as either:
          - there are new/unreviewed submissions (newSubmissionsCount > 0), OR
          - a debug flag is present in the URL (?showSubmissionsDot=1) so you can easily see the dot working on localhost.
        */}
        {/* Submissions card: pulse only when there are new / unreviewed submissions or revisions */}
        <div 
          className={`dashboard-home-main-card dashboard-home-submissions-card ${
            newSubmissionsCount > 0 ? 'has-new' : ''
          }`}
          onClick={() => setActiveTab && setActiveTab('handin')}
        >
          <div className="dashboard-home-card-icon">
            <svg className="dashboard-home-lucide-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14,2 14,8 20,8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10,9 9,9 8,9"/>
            </svg>
          </div>
          <div className="dashboard-home-card-content">
            <h3>Submissions</h3>
            <p>Review and approve content from creators</p>
            {newSubmissionsCount > 0 && (
              <div className="dashboard-home-submission-badge">
                {newSubmissionsCount} new submission{newSubmissionsCount !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="dashboard-home-cta-section">
        <button 
          className="dashboard-home-inspiration-btn"
          onClick={() => setShowTrendingModal(true)}
          title="Browse this week's targeted trending video styles"
        >
          <TrendingUp className="dashboard-home-btn-icon" size={20} />
          Get Inspired / Targeted Trending Styles
        </button>
      </div>

      {/* Feedback Button */}
      <div className="dashboard-home-feedback-wrapper">
        <button 
          type="button" 
          className="dashboard-home-feedback-btn" 
          onClick={() => setShowFeedbackModal(true)}
        >
          <MessageSquare size={14} />
          <span>Feedback</span>
        </button>
      </div>

      {/* Targeted Trending Styles Modal */}
      {showTrendingModal && (
        <div className="dashboard-home-trends-mockup-modal-overlay" onClick={() => setShowTrendingModal(false)}>
          <div className="dashboard-home-trends-mockup-modal" onClick={e => e.stopPropagation()}>
            <button 
              className="dashboard-home-trends-mockup-modal-close"
              onClick={() => setShowTrendingModal(false)}
            >
              ×
            </button>
            <h3>Trending Styles</h3>
            
            {loadingVideos ? (
              <div className="dashboard-home-trends-loading">
                <div className="dashboard-home-trends-spinner"></div>
                <p>Loading this week's trending styles...</p>
              </div>
            ) : targetedTrendingVideos.length === 0 ? (
              <div className="dashboard-home-trends-empty">
                <p>No trending videos available this week. Check back soon!</p>
              </div>
            ) : (
              <div className="dashboard-home-trends-mockup-list">
                {targetedTrendingVideos.map((video) => (
                  <div key={video.id} className="dashboard-home-trends-mockup-video">
                    <div className="dashboard-home-video-wrapper">
                      <video 
                        id={`trending-video-${video.id}`}
                        controls
                        loop 
                        playsInline
                        preload="metadata"
                        onPlay={() => handleVideoPlay(video.id)}
                        onPause={() => handleVideoPause(video.id)}
                        onEnded={() => handleVideoPause(video.id)}
                      >
                        <source src={video.videoUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                      {!playingVideos[video.id] && (
                        <div 
                          className="dashboard-home-video-play-overlay"
                          onClick={() => toggleVideoPlay(video.id)}
                        >
                          <div className="dashboard-home-play-button">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="dashboard-home-trends-mockup-title">{video.title}</div>
                    <div className="dashboard-home-trends-mockup-desc">{video.description}</div>
                    <button 
                      className={`dashboard-home-trends-mockup-use-btn ${!hasCampaigns ? 'disabled' : ''}`}
                      onClick={() => handleUseTargetedStyle(video)}
                      disabled={!hasCampaigns}
                      title={!hasCampaigns ? 'Create a campaign first to use this style' : 'Use this trending style for your campaign'}
                    >
                      Use This Style
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Targeted Trending Style Script Panel */}
      <TargetedTrendingStyleScriptPanel
        open={showTargetedScriptPanel}
        onClose={() => setShowTargetedScriptPanel(false)}
        targetedVideo={selectedTargetedVideo}
        onScriptGenerated={handleScriptGenerated}
      />

      {/* Client Script Panel for editing generated script */}
      {openScriptEditor && generatedScriptData && (
        <ClientScriptPanel
          open={openScriptEditor}
          onClose={() => {
            setOpenScriptEditor(false);
            setGeneratedScriptData(null);
          }}
          script={{
            id: Date.now(),
            name: generatedScriptData.scriptName,
            lines: generatedScriptData.scriptContent, // This should be the parsed lines array
            settings: generatedScriptData.settings,
            campaignId: generatedScriptData.campaignId,
            briefId: generatedScriptData.briefId
          }}
          initialCampaign={generatedScriptData.campaignId}
          onScriptSaved={() => {
            setOpenScriptEditor(false);
            setGeneratedScriptData(null);
            // Optionally show success message
          }}
        />
      )}

      {/* Feedback Modal */}
      <GeneralFeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        user={getAuth().currentUser}
      />

    </div>
  );
}

export default DashboardHome;
